import React, { useEffect, useRef, useState, useMemo, Suspense, lazy } from 'react';
import { Clock, Send, X, TrendingUp, Sparkles, Package } from 'lucide-react';
import { Link } from 'react-router-dom';

const Globe = lazy(() => import('react-globe.gl'));

// Béziers warehouse coordinates
const ORIGIN = { lat: 43.3441, lng: 3.2151, name: 'Béziers (Entrepôt)' };

// Destinations with illustrative metrics — feel free to tune!
const DESTINATIONS = [
  { slug: 'guadeloupe',  name: 'Guadeloupe',     flag: '🇬🇵', lat: 16.265,  lng: -61.551, delay: '6-10 j',  priceFrom: 17.90, packages: 1240, color: '#06b6d4' },
  { slug: 'martinique',  name: 'Martinique',     flag: '🇲🇶', lat: 14.641,  lng: -61.024, delay: '6-10 j',  priceFrom: 17.90, packages: 980,  color: '#06b6d4' },
  { slug: 'reunion',     name: 'La Réunion',     flag: '🇷🇪', lat: -21.115, lng: 55.536,  delay: '7-12 j',  priceFrom: 19.90, packages: 720,  color: '#06b6d4' },
  { slug: 'guyane',      name: 'Guyane',         flag: '🇬🇫', lat: 3.933,   lng: -53.125, delay: '8-14 j',  priceFrom: 22.90, packages: 410,  color: '#06b6d4' },
  { slug: 'mayotte',     name: 'Mayotte',        flag: '🇾🇹', lat: -12.827, lng: 45.166,  delay: '10-15 j', priceFrom: 24.90, packages: 280,  color: '#06b6d4' },
  { slug: 'polynesie',   name: 'Polynésie F.',   flag: '🇵🇫', lat: -17.679, lng: -149.406,delay: '14-21 j', priceFrom: 49.00, packages: 95,   color: '#0ea5e9' },
  { slug: 'nouvelle-caledonie', name: 'Nouv.-Calédonie', flag: '🇳🇨', lat: -20.904, lng: 165.618, delay: '14-21 j', priceFrom: 49.00, packages: 70, color: '#0ea5e9' },
  { slug: 'maroc',       name: 'Maroc',          flag: '🇲🇦', lat: 31.792,  lng: -7.092,  delay: '5-8 j',   priceFrom: 14.90, packages: 1850, color: '#22c55e' },
  { slug: 'algerie',     name: 'Algérie',        flag: '🇩🇿', lat: 28.034,  lng: 1.659,   delay: '6-10 j',  priceFrom: 16.90, packages: 1620, color: '#22c55e' },
  { slug: 'tunisie',     name: 'Tunisie',        flag: '🇹🇳', lat: 33.886,  lng: 9.537,   delay: '5-8 j',   priceFrom: 14.90, packages: 1100, color: '#22c55e' },
  { slug: 'senegal',     name: 'Sénégal',        flag: '🇸🇳', lat: 14.497,  lng: -14.452, delay: '7-11 j',  priceFrom: 18.90, packages: 640,  color: '#22c55e' },
  { slug: 'cote-ivoire', name: 'Côte d\'Ivoire', flag: '🇨🇮', lat: 7.539,   lng: -5.547,  delay: '8-12 j',  priceFrom: 19.90, packages: 520,  color: '#22c55e' },
  { slug: 'cameroun',    name: 'Cameroun',       flag: '🇨🇲', lat: 7.369,   lng: 12.354,  delay: '9-13 j',  priceFrom: 21.90, packages: 380,  color: '#22c55e' },
  { slug: 'mali',        name: 'Mali',           flag: '🇲🇱', lat: 17.570,  lng: -3.996,  delay: '9-14 j',  priceFrom: 22.90, packages: 290,  color: '#22c55e' },
  { slug: 'gabon',       name: 'Gabon',          flag: '🇬🇦', lat: -0.803,  lng: 11.609,  delay: '10-14 j', priceFrom: 24.90, packages: 210,  color: '#22c55e' },
  { slug: 'congo',       name: 'Congo',          flag: '🇨🇬', lat: -0.228,  lng: 15.827,  delay: '11-15 j', priceFrom: 25.90, packages: 180,  color: '#22c55e' },
  { slug: 'rdc',         name: 'RD Congo',       flag: '🇨🇩', lat: -4.038,  lng: 21.758,  delay: '12-16 j', priceFrom: 26.90, packages: 150,  color: '#22c55e' },
  { slug: 'madagascar',  name: 'Madagascar',     flag: '🇲🇬', lat: -18.766, lng: 46.869,  delay: '12-18 j', priceFrom: 27.90, packages: 220,  color: '#22c55e' },
];

const TOTAL_PACKAGES = DESTINATIONS.reduce((s, d) => s + d.packages, 0); // eslint-disable-line no-unused-vars

export default function InteractiveGlobe() {
  const globeRef = useRef(null);
  const containerRef = useRef(null);
  const [selected, setSelected] = useState(null);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [globeReady, setGlobeReady] = useState(false);
  const [inView, setInView] = useState(false); // Only mount when scrolled into view (perf)

  // IntersectionObserver: mount the globe only when scrolled into view
  useEffect(() => {
    if (!containerRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin: '200px' }
    );
    obs.observe(containerRef.current);
    return () => obs.disconnect();
  }, []);

  // Pause auto-rotation when tab hidden (saves battery / GPU)
  useEffect(() => {
    const onVisibility = () => {
      if (!globeRef.current) return;
      const controls = globeRef.current.controls?.();
      if (controls) controls.autoRotate = !document.hidden && !selected;
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [selected]);

  // Responsive sizing — bigger globe
  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        const isMobileSize = width < 640;
        const height = isMobileSize ? Math.min(window.innerHeight * 0.6, 520) : Math.min(width * 0.65, 720);
        setDimensions({ width, height });
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  // Arcs from Béziers to every destination
  const arcs = useMemo(() => DESTINATIONS.map(d => ({
    startLat: ORIGIN.lat,
    startLng: ORIGIN.lng,
    endLat: d.lat,
    endLng: d.lng,
    color: [d.color, d.color],
    dest: d,
  })), []);

  // Points: origin + all destinations — bigger for easier mobile tapping
  const points = useMemo(() => [
    { lat: ORIGIN.lat, lng: ORIGIN.lng, name: ORIGIN.name, size: 1.4, color: '#fbbf24', isOrigin: true },
    ...DESTINATIONS.map(d => ({
      lat: d.lat, lng: d.lng, name: d.name,
      size: Math.min(0.8 + (d.packages / 2000) * 0.8, 1.6),  // baseline raised
      color: d.color, dest: d
    }))
  ], []);

  // Rings (pulse) on each destination — bigger
  const rings = useMemo(() => DESTINATIONS.map(d => ({
    lat: d.lat, lng: d.lng, maxR: 6, propagationSpeed: 2.2, repeatPeriod: 1800, color: d.color
  })), []);

  // Setup globe controls once ready
  useEffect(() => {
    if (!globeReady || !globeRef.current) return;
    const controls = globeRef.current.controls();
    if (controls) {
      controls.autoRotate = true;
      controls.autoRotateSpeed = 0.55;
      controls.enableZoom = false;
    }
    // Center on Atlantic so all destinations visible
    globeRef.current.pointOfView({ lat: 18, lng: -10, altitude: 2.4 }, 1500);
  }, [globeReady]);

  const handlePointClick = (point) => {
    if (point.isOrigin) return;
    if (point.dest) {
      setSelected(point.dest);
      // Pause rotation and zoom to point
      if (globeRef.current) {
        const controls = globeRef.current.controls();
        if (controls) controls.autoRotate = false;
        globeRef.current.pointOfView({ lat: point.lat, lng: point.lng, altitude: 1.8 }, 1200);
      }
    }
  };

  const closeDetail = () => {
    setSelected(null);
    if (globeRef.current) {
      const controls = globeRef.current.controls();
      if (controls) controls.autoRotate = true;
      globeRef.current.pointOfView({ lat: 18, lng: -10, altitude: 2.4 }, 1200);
    }
  };

  return (
    <div className="relative">
      {/* Globe container */}
      <div
        ref={containerRef}
        className="relative w-full rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(30,58,138,0.4)] border border-blue-900/30"
        style={{ minHeight: '420px', background: 'radial-gradient(ellipse at center, #1e3a8a 0%, #0f172a 70%, #020617 100%)' }}
      >
        {/* Soft stars background, less aggressive */}
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{
          background: 'radial-gradient(2px 2px at 20px 30px, white, transparent), radial-gradient(1.5px 1.5px at 60px 70px, white, transparent), radial-gradient(1px 1px at 90px 120px, white, transparent), radial-gradient(1.5px 1.5px at 200px 200px, white, transparent), radial-gradient(2px 2px at 300px 150px, white, transparent), radial-gradient(1px 1px at 450px 350px, white, transparent), radial-gradient(1.5px 1.5px at 550px 100px, white, transparent), radial-gradient(1px 1px at 650px 250px, white, transparent), radial-gradient(2px 2px at 750px 400px, white, transparent), radial-gradient(1px 1px at 850px 500px, white, transparent)',
          backgroundRepeat: 'repeat',
          backgroundSize: '900px 600px',
          animation: 'twinkle 6s ease-in-out infinite alternate'
        }} />
        <style>{`@keyframes twinkle { 0%{opacity:.15} 50%{opacity:.5} 100%{opacity:.2} }`}</style>

        {/* Top-left badge */}
        <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10 flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 text-white text-xs md:text-sm">
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500"></span>
          </span>
          <span className="font-semibold">En direct depuis Béziers</span>
        </div>

        {/* Hint to interact */}
        {!selected && (
          <div className="absolute bottom-4 right-4 md:bottom-6 md:right-6 z-10 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-4 py-2 text-white text-xs flex items-center gap-2 animate-pulse">
            <Sparkles className="w-3.5 h-3.5 text-amber-300" />
            <span className="hidden sm:inline">Cliquez sur une destination</span>
            <span className="sm:hidden">Touchez un point</span>
          </div>
        )}

        {/* Globe (mounted when scrolled into view) */}
        {inView ? (
          <Suspense fallback={
            <div className="flex items-center justify-center" style={{ height: dimensions.height }}>
              <div className="text-white/60 text-sm">Chargement du globe…</div>
            </div>
          }>
            <Globe
              ref={globeRef}
              width={dimensions.width}
              height={dimensions.height}
              backgroundColor="rgba(0,0,0,0)"
              globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
              bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
              atmosphereColor="#3b82f6"
              atmosphereAltitude={0.28}
              onGlobeReady={() => setGlobeReady(true)}

              arcsData={arcs}
              arcColor="color"
              arcAltitude={0.32}
              arcStroke={0.6}
              arcDashLength={0.5}
              arcDashGap={1.2}
              arcDashAnimateTime={3000}
              arcsTransitionDuration={0}

              pointsData={points}
              pointLat="lat"
              pointLng="lng"
              pointColor="color"
              pointAltitude={0.04}
              pointRadius="size"
              pointResolution={12}
              pointLabel={p => `<div style="background:rgba(15,23,42,.95);color:white;padding:10px 14px;border-radius:10px;font-family:sans-serif;font-size:14px;border:1px solid rgba(96,165,250,.3);box-shadow:0 4px 12px rgba(0,0,0,.4);">
                <strong style="font-size:15px;">${p.name}</strong>${p.dest ? `<br/><span style="color:#93c5fd;font-size:12px;">${p.dest.packages}+ colis · dès ${p.dest.priceFrom}€ · ${p.dest.delay}</span>` : ''}
              </div>`}
              onPointClick={handlePointClick}

              ringsData={rings}
              ringColor={(r) => () => r.color}
              ringMaxRadius="maxR"
              ringPropagationSpeed="propagationSpeed"
              ringRepeatPeriod="repeatPeriod"
            />
          </Suspense>
        ) : (
          <div className="flex items-center justify-center" style={{ height: dimensions.height }}>
            <div className="text-white/60 text-sm">Chargement du globe…</div>
          </div>
        )}

        {/* Detail panel overlay */}
        {selected && (
          <div className="absolute inset-x-3 bottom-3 md:inset-x-auto md:right-6 md:bottom-6 md:max-w-sm z-20 animate-in slide-in-from-bottom duration-300">
            <div className="relative bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/40 p-5">
              <button
                onClick={closeDetail}
                className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                aria-label="Fermer"
                data-testid="globe-close-detail"
              >
                <X className="w-4 h-4 text-slate-600" />
              </button>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-5xl drop-shadow-md">{selected.flag}</span>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Destination</p>
                  <h3 className="font-heading font-black text-2xl text-slate-900 leading-tight">{selected.name}</h3>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-4">
                <DetailMini icon={Package} value={`${selected.packages}+`} label="Colis" />
                <DetailMini icon={Clock} value={selected.delay} label="Délai" />
                <DetailMini icon={TrendingUp} value={`${selected.priceFrom}€`} label="Dès" />
              </div>
              <Link to={`/destination/${selected.slug}`} className="block">
                <button className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50 transition-all" data-testid={`globe-send-${selected.slug}`}>
                  <Send className="w-4 h-4" />
                  Envoyer vers {selected.name}
                </button>
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Below globe: quick destination grid — bigger tap targets for mobile */}
      <div className="mt-6 max-w-5xl mx-auto">
        <p className="text-center text-xs sm:text-sm text-slate-500 mb-4 uppercase tracking-wider font-bold">
          Touchez une destination
        </p>
        <div className="flex flex-wrap justify-center gap-2 sm:gap-2.5">
          {DESTINATIONS.map(d => (
            <button
              key={d.slug}
              onClick={() => handlePointClick({ lat: d.lat, lng: d.lng, dest: d })}
              className="group flex items-center gap-2 bg-white border-2 border-slate-200 hover:border-blue-500 hover:bg-blue-50 active:bg-blue-100 px-3.5 py-2.5 rounded-full text-sm transition-all hover:shadow-md hover:-translate-y-0.5 touch-manipulation"
              data-testid={`globe-chip-${d.slug}`}
            >
              <span className="text-xl">{d.flag}</span>
              <span className="font-semibold text-slate-700 group-hover:text-blue-700">{d.name}</span>
              <span className="hidden sm:inline text-xs text-slate-400 group-hover:text-blue-500">· {d.delay}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function DetailMini({ icon: Icon, value, label }) {
  return (
    <div className="bg-slate-50 rounded-xl p-3 text-center border border-slate-100">
      <Icon className="w-4 h-4 text-blue-600 mx-auto mb-1" />
      <p className="font-heading font-bold text-base text-slate-900">{value}</p>
      <p className="text-[10px] uppercase tracking-wide text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function MobileFallback({ height, onActivate, isMobile }) { // eslint-disable-line no-unused-vars
  return null;
}
